'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Retarde le montage d'un contenu coûteux (carte Leaflet chargée depuis le
 * paquet npm, widget tiers…) jusqu'à ce qu'il approche du viewport, au lieu de le
 * charger dès l'hydratation de la page.
 *
 * Contexte (audit perf sept. 2026) : FranceMap et MiniMap appellent
 * chargerLeaflet() dans un useEffect qui se déclenche dès leur montage. Sur
 * la page d'accueil et les fiches projet, ce montage a lieu au chargement
 * initial même quand la carte est hors écran (section "aperçu projets" en
 * bas de la home, mini-carte "Localisation" en bas de fiche) : Lighthouse
 * mobile mesurait ~865 ms de script rien que pour charger et exécuter
 * leaflet.js + leaflet.markercluster.js avant que l'utilisateur n'ait
 * scrollé jusqu'à la carte. LazyMount ne change rien à la carte elle-même :
 * il retarde juste le moment où elle apparaît dans l'arbre React.
 *
 * `placeholderClassName` doit reprendre les classes de taille du contenu
 * réel (hauteur notamment) pour ne provoquer aucun décalage de mise en page
 * à l'arrivée dans le viewport.
 */
export default function LazyMount({
  children,
  placeholderClassName,
  rootMargin = '400px',
}: {
  children: ReactNode
  placeholderClassName?: string
  rootMargin?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (visible) return
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      // Pas d'IntersectionObserver (très vieux navigateur) : on monte tout de suite.
      setVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [visible, rootMargin])

  if (visible) return <>{children}</>
  return <div ref={ref} className={placeholderClassName} aria-hidden="true" />
}
